import type { Breakpoint, ResponsiveValue } from "@emkit/builder-core";

/**
 * These helpers let inspectors edit ResponsiveValue<T> fields per
 * breakpoint. The data model stays exactly what @emkit/builder-core
 * defines — a scalar when the same value applies everywhere, or an
 * object `{ desktop, tablet?, mobile? }` with fallback mobile → tablet
 * → desktop at render time.
 *
 * The inspector UI is single-breakpoint at a time; the user flips a
 * breakpoint switch in the toolbar and fields reflect that slice.
 */

function isResponsiveObject<T>(
  v: ResponsiveValue<T> | undefined,
): v is { desktop: T; tablet?: T; mobile?: T } {
  return typeof v === "object" && v !== null && !Array.isArray(v) && "desktop" in v;
}

/**
 * Read the effective value at a breakpoint. If the field is a plain
 * scalar it's returned as-is. If it's a responsive object, falls back
 * mobile → tablet → desktop.
 */
export function readAtBreakpoint<T>(
  v: ResponsiveValue<T> | undefined,
  bp: Breakpoint,
): T | undefined {
  if (v === undefined) return undefined;
  if (!isResponsiveObject<T>(v)) return v;
  if (bp === "mobile") return v.mobile ?? v.tablet ?? v.desktop;
  if (bp === "tablet") return v.tablet ?? v.desktop;
  return v.desktop;
}

/**
 * Write an override at a breakpoint. Rules:
 *  - Writing at desktop when current is scalar → keep scalar.
 *  - Writing at tablet/mobile on a scalar → upgrade to object form so
 *    desktop keeps its value and the new override lands.
 *  - Writing at any breakpoint on an object form → merge.
 */
export function writeAtBreakpoint<T>(
  current: ResponsiveValue<T> | undefined,
  bp: Breakpoint,
  value: T,
): ResponsiveValue<T> {
  if (current === undefined) {
    if (bp === "desktop") return value;
    // Fallback baseline so desktop isn't left undefined.
    return { desktop: value, [bp]: value } as ResponsiveValue<T>;
  }

  if (!isResponsiveObject<T>(current)) {
    if (bp === "desktop") return value;
    return { desktop: current as T, [bp]: value } as ResponsiveValue<T>;
  }

  return { ...current, [bp]: value };
}

/**
 * Remove the override at a specific breakpoint, causing that
 * breakpoint to fall back to its parent. If the only remaining slot
 * is desktop, collapses to a scalar.
 */
export function clearAtBreakpoint<T>(
  current: ResponsiveValue<T> | undefined,
  bp: Breakpoint,
): ResponsiveValue<T> | undefined {
  if (current === undefined) return undefined;
  if (!isResponsiveObject<T>(current)) return current;
  const next = { ...current } as { desktop: T; tablet?: T; mobile?: T };
  if (bp === "mobile") delete next.mobile;
  else if (bp === "tablet") delete next.tablet;
  else return current; // cannot clear desktop
  if (next.mobile === undefined && next.tablet === undefined) {
    return next.desktop;
  }
  return next as ResponsiveValue<T>;
}

/**
 * True when the given breakpoint has an explicit override (vs.
 * inheriting from a higher breakpoint).
 */
export function hasExplicitOverride<T>(
  v: ResponsiveValue<T> | undefined,
  bp: Breakpoint,
): boolean {
  if (v === undefined) return false;
  if (!isResponsiveObject<T>(v)) return bp === "desktop";
  if (bp === "desktop") return true;
  if (bp === "tablet") return v.tablet !== undefined;
  return v.mobile !== undefined;
}
